/**
 * Road-sign arrow glyphs.
 *
 * One path set drawn in the style of motorway signage: thick stroke, rounded
 * joins, high contrast. The same geometry is reused at two sizes - the large
 * maneuver sign and the per-lane chips - so the visual language stays
 * consistent. All paths live in a 0 0 64 64 box, drawn pointing up (=straight)
 * and composed/rotated for the variants.
 */

import type { LaneIndication } from '../core/types';

const P = {
  straight: 'M32 54 V22 M20 32 L32 16 L44 32',
  slightRight: 'M28 54 V38 C28 30 34 26 40 24 M32 14 L46 18 L42 32',
  slightLeft: 'M36 54 V38 C36 30 30 26 24 24 M32 14 L18 18 L22 32',
  right: 'M20 54 V40 C20 32 26 30 36 30 L44 30 M36 18 L50 30 L36 42',
  left: 'M44 54 V40 C44 32 38 30 28 30 L20 30 M28 18 L14 30 L28 42',
  sharpRight: 'M24 54 V34 C24 26 30 24 38 28 L46 34 M46 18 L50 36 L32 34',
  sharpLeft: 'M40 54 V34 C40 26 34 24 26 28 L18 34 M18 18 L14 36 L32 34',
  uturn: 'M40 54 V30 C40 20 24 20 24 30 V44 M14 34 L24 48 L34 34',
  merge: 'M24 54 C24 42 30 38 32 30 M40 54 C40 42 34 38 32 30 M32 30 V22 M22 26 L32 12 L42 26',
  forkRight: 'M28 54 V40 C28 32 34 30 40 26 M28 40 C28 32 24 30 20 26 M32 14 L46 20 L40 32',
  forkLeft: 'M36 54 V40 C36 32 30 30 24 26 M36 40 C36 32 40 30 44 26 M32 14 L18 20 L24 32',
  rampRight: 'M26 54 V36 C26 28 32 26 40 24 M34 14 L48 20 L42 32',
  rampLeft: 'M38 54 V36 C38 28 32 26 24 24 M30 14 L16 20 L22 32',
  roundabout:
    'M32 54 V44 M32 16 a12 12 0 1 0 0.01 0 M24 20 L32 8 L40 20',
  arrive: 'M32 50 V22 M20 36 L32 50 L44 36 M18 56 H46',
} as const;

type GlyphKey = keyof typeof P;

/** Map Mapbox maneuver (type, modifier) to a glyph. */
export function glyphForManeuver(type: string, modifier?: string, drivingSide: 'left' | 'right' = 'left'): GlyphKey {
  const m = (modifier ?? '').toLowerCase();
  switch (type) {
    case 'arrive':
      return 'arrive';
    case 'roundabout':
    case 'rotary':
    case 'roundabout turn':
    case 'exit roundabout':
    case 'exit rotary':
      return 'roundabout';
    case 'merge':
      return 'merge';
    case 'on ramp':
      return m.includes('left') ? 'rampLeft' : 'rampRight';
    case 'off ramp':
      // Thailand drives on the left: unmarked exits leave leftwards.
      return m.includes('right') ? 'rampRight' : drivingSide === 'left' ? 'rampLeft' : 'rampRight';
    case 'fork':
      return m.includes('left') ? 'forkLeft' : 'forkRight';
  }
  switch (m) {
    case 'uturn':
      return 'uturn';
    case 'sharp right':
      return 'sharpRight';
    case 'sharp left':
      return 'sharpLeft';
    case 'right':
      return 'right';
    case 'left':
      return 'left';
    case 'slight right':
      return 'slightRight';
    case 'slight left':
      return 'slightLeft';
    default:
      return 'straight';
  }
}

export function glyphForLane(indication: LaneIndication): GlyphKey {
  switch (indication) {
    case 'left':
      return 'left';
    case 'right':
      return 'right';
    case 'slight left':
      return 'slightLeft';
    case 'slight right':
      return 'slightRight';
    case 'sharp left':
      return 'sharpLeft';
    case 'sharp right':
      return 'sharpRight';
    case 'uturn':
      return 'uturn';
    default:
      return 'straight';
  }
}

/** Render a glyph as an SVG string. */
export function arrowSvg(key: GlyphKey, cls = ''): string {
  return `<svg class="${cls}" viewBox="0 0 64 64" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="7" stroke-linecap="round" stroke-linejoin="round">${
    key === 'roundabout'
      ? `<path d="M32 56 V46" /><circle cx="32" cy="30" r="13" /><path d="M24 14 L32 4 L40 14" fill="currentColor" stroke="none"/>`
      : `<path d="${P[key]}" />`
  }</svg>`;
}

/**
 * A lane chip: the full set of painted arrows for that lane, with the
 * direction that stays on route emphasised and the rest dimmed - exactly how
 * overhead lane signs read.
 */
export function laneChipSvg(
  indications: LaneIndication[],
  validIndication: LaneIndication | undefined,
  valid: boolean,
): string {
  const list = indications.length ? indications : ['straight' as LaneIndication];
  const inner = list
    .map((ind) => {
      const isTheOne = valid && (validIndication === undefined || ind === validIndication || list.length === 1);
      return `<span class="lane-chip__glyph ${isTheOne ? 'is-valid' : ''}">${arrowSvg(glyphForLane(ind))}</span>`;
    })
    .join('');
  return `<span class="lane-chip ${valid ? 'lane-chip--valid' : 'lane-chip--invalid'}">${inner}</span>`;
}
